import type { Settings as LayoutSettings } from '@ant-design/pro-layout';
import { PageLoading } from '@ant-design/pro-layout';
import type { RunTimeLayoutConfig } from 'umi';
import { history } from 'umi';
import RightContent from '@/components/Framework/RightContent';
import Footer from '@/components/Framework/Footer';
import { getUserInfo } from '@/services/authority/login/login';
import type { SimplePromise } from '@bluevision/tools';
import NoAccess from './components/Framework/NoAccess/NoAccess';
import siderLogo from '@/assets/bluefocus_ai_logo_white_color.png';
import type { AccessInfo } from '@/typings/user';
import { version } from '../package.json';
import * as Sentry from '@sentry/browser';
import ErrorBoundary from '@/components/ErrorBoundary';
import { checkReloadApp } from './utils/versionCheck';
import { isAllowUrl, isIgnoreError } from './utils/sentry';

import { getMediaMap } from '@/utils/common/newMedia';

import '@bluevision/components/index.css';
import { identifyUser, initPostHog } from './posthog';

// 若需要修改根容器，可结合 rootContainer（可选）
export function rootContainer(container: React.ReactElement) {
  initPostHog(); // 也可在此初始化（确保只执行一次）
  return container;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__APP_VERSION = version;

export const initialStateConfig = {
  loading: <PageLoading />,
};

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: AccessInfo;
  fetchUserInfo?: SimplePromise;
}> {
  if (history.location.pathname !== LoginPath) {
    const currentUser = await getUserInfo();

    // 设置 user 到 sentry 上，跟随每一次错误上报
    if (currentUser) {
      Sentry.setUser({
        id: currentUser.id,
        username: currentUser.name,
        email: currentUser.email,
      });

      identifyUser(currentUser?.email, {
        name: currentUser?.name,
        email: currentUser.email,
      });
    }

    return {
      currentUser,
      settings: {},
    };
  }
  return {
    settings: {},
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    layout: 'side',
    navTheme: 'dark',
    logo: siderLogo,
    /**
     * 侧边栏顶部展示 公司标 + 产品名。侧边栏仅 196px 宽，横向排不下（字标 172px +
     * 分隔线 + 「BlueGrowth Cloud Admin」约 350px），故改为上下堆叠，样式见 overwrite.less。
     * 收起态两者都放不下，整块不渲染。
     */
    menuHeaderRender: (logoDom, titleDom, props) =>
      props?.collapsed ? null : (
        <a>
          {logoDom}
          {titleDom}
        </a>
      ),
    ErrorBoundary: ErrorBoundary,
    rightContentRender: () => <RightContent />,
    disableContentMargin: false,
    footerRender: () => <Footer />,
    onPageChange: async () => {
      await getMediaMap();
      checkReloadApp();
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== LoginPath) {
        history.push(LoginPath);
      }
    },
    unAccessible: <NoAccess />,
    // noFound: <NoFoundPage/>,
    ...initialState?.settings,
  };
};

/** 全局非 React 错误捕获 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
window.addEventListener('error', (event: any) => {
  const errMsg = event?.message;
  if (isIgnoreError(errMsg)) {
    return;
  }
  if (!isAllowUrl(window.location.origin)) {
    return;
  }
  Sentry?.captureException(event, {
    extra: { type: 'global error catch' },
  });
});
